import React, {Component, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { chain, toPairs, fromPairs } from 'lodash';

import { Pill, Col, Row } from 'bs';
import { Icon } from 'components/Icon';
import { BlockLink } from 'components/BlockLink';
import ModalBackground from 'components/ModalBackground';
import {useSelector} from "react-redux";
import { useDispatch } from 'react-redux';

const StyledCard = styled.div`
    border: none;
    margin: 0 15px 15px 0;

    .container {
        padding: 0;
    }
    .row {
        margin-left: 0;
        margin-right: 0;
    }
    .row.section-title {
        margin-left: -15px;
        margin-right: -15px;
    }

    .card-body {
        padding: 15px;
        h3 {
            font-size: 1.25rem;
            margin: 0.5rem 0 0;
            padding: 0.5rem 0;
        }
        .section-title {
            .col-12 {
                padding: 0;
            }
        }
    }

    .switcher-link {
        line-height: 1rem;
        font-size: 0.9rem;
        text-align: center;
        cursor: pointer;

        a {
            padding: 1.5rem;
        }

        &.active {
            color: ${props => props.theme.color.primary};
            &:hover {
                color: ${props => props.theme.color.primary};
            }
        }

        &:hover {
            cursor: pointer;
            background-color: ${props => props.theme.color.light};
        }

        svg[class*=' fa'] {
            color: ${props => props.theme.color.primary};
            display: block;
            font-size: 2rem;
            margin: 0 auto 0.45rem;
            opacity: 1;
        }
        a {
            text-decoration: none;
        }
    }
    @media (max-width: 767px) {
        margin: 15px 0;
    }
    #row-modal & {
        margin: 0 -15px;
    }
    .app-link--badge-container {
        top: 1rem;
        position: absolute;
        left: 50%;
    }
`;

const AppBadgeCount = ({ count, details }) => (
    <div className='app-link--badge-container'>
        <Pill flavor='danger'>
            {count}
        </Pill>
        {details && <Pill flavor='warning'>{details}</Pill>}
    </div>

);

class AppLink extends Component {
    static defaultProps = {
        icon: 'bullseye',
        count: undefined,
        label: 'App Name',
        active: false,
        tabIndex: '1',
        path: '/',
        id: 'app_name',
    };

    render() {
        const { icon, id, callback, label, active, path, tabIndex, count } = this.props;
        const className = active ? 'switcher-link active' : 'switcher-link';
        return (
            <Col xs={6} sm={3} md={2} className={className} tabIndex={tabIndex}>
                <BlockLink to={path} onClick={e => callback()} id={`${id}-link`}>
                    {count && <AppBadgeCount count={count.badge_count} details={count.badge_count_details} />}
                    <Icon name={icon} />
                    <span className='label'>{label}</span>
                </BlockLink>
            </Col>
        );
    }
}

export const locationsMatch = (current = {}, next = {}) => {
    // simple check to see if two location objects are identical
    return JSON.stringify(current) === JSON.stringify(next);
};

export const matchApp = (pathname = '', apps = {}) => {
    // Find the app whose 'path' key matches current pathname
    let match = null;
    Object.keys(apps).forEach(key => {
        const app = apps[key];
        if (pathname.startsWith(app.path)) {
            match = key;
        }
    });
    return match;
};

export class AppLocationManager extends Component {
    static propTypes = {
        apps: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        updateAppLocation: PropTypes.func.isRequired,
        currentTitle: PropTypes.string,
    };

    updateAppLocation(location) {
        const app = matchApp(location.pathname, this.props.apps);
        const title = this.props.currentTitle;
        const path = `${location.pathname}${location.search}`;
        if (app) {
            this.props.updateAppLocation(app, path, title);
        }
    }

    componentDidMount() {
        this.updateAppLocation(this.props.location);
    }

    componentDidUpdate(prevProps, prevState) {
        // if the location path + search changed, update it
        const matchingLocations = locationsMatch(
            this.props.location,
            prevProps.location
        );
        if (!matchingLocations || this.props.currentTitle !== prevProps.currentTitle) {
            this.updateAppLocation(this.props.location);
        }
    }

    render() {
        return null;
    }
}

const setEmployerGroup = (eg) => ({
    type: "UPDATE_BREADCRUMBS",
    employergroup: eg,
    employer: null,
    app: null
})

const setEmployer = (e) => ({
    type: "UPDATE_BREADCRUMBS",
    employer: e,
    app: null
})

const setApp = (a, clear) => {
    const action = {
        type: "UPDATE_BREADCRUMBS",
        app: a
    }
    if (clear) {
        action["employer"] = null;
        action["employergroup"] = null;
    }
    return action;
}

const clearBreadcrumbs = () => ({
    type: "UPDATE_BREADCRUMBS",
    employergroup: null,
    employer: null,
    app: null
})

export const Breadcrumbs = ({employergroup, employer, app, clear}) => {
    const bc = useSelector(state => state.switcher.breadcrumbs);
    const dispatch = useDispatch();
    useEffect(() => {
        if (employergroup && ((!bc.employergroup || employergroup.title != bc.employergroup.title) || bc.employer || bc.app)) {
            dispatch(setEmployerGroup(employergroup))
        }
        else if (employer && ((!bc.employer || employer.title != bc.employer.title) || bc.app)) {
            dispatch(setEmployer(employer))
        } else if (app && (!bc.app || app.title != bc.app.title)) {
            dispatch(setApp(app, clear))
        }
        else if (!employergroup && !employer && !app && (bc.employergroup || bc.employer || bc.app)) {
            dispatch(clearBreadcrumbs())
        }
    }, [])
}

class AppSwitcher extends Component {
    static propTypes = {
        apps: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
        dismiss: PropTypes.func.isRequired,
        updateAppLocation: PropTypes.func.isRequired,
        clearAppTrail: PropTypes.func.isRequired,
        fetchUser: PropTypes.func.isRequired,
        showCounts: PropTypes.bool,
        currentApp: PropTypes.string,
        currentTitle: PropTypes.string,
        visible: PropTypes.bool,
        inModal: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.postSelectCallback = this.postSelectCallback.bind(this);
    }

    componentDidMount() {
        if (this.props.showCounts) {
            this.props.fetchUser({badges: true});
        }
    }

    postSelectCallback() {
        this.props.clearAppTrail();
        this.props.dismiss();
    }

    render() {
        if (!this.props.visible) {
            return null;
        }

        const {
            currentApp,
            apps,
            user,
            showCounts = false,
            inModal = true,
        } = this.props;
        const defaultGroup = 'General';
        const groupedApps = chain(apps)
            .values()
            .groupBy('group')
            .entries()
            .map(([label, apps]) => (
                label === 'undefined' ? [defaultGroup, apps] : [label, apps]
            ))
            .sortBy(([label, apps]) => label !== 'General')
            .value();
        const sortByOrder = (one, two) => {
            return (two.order || 0) - (one.order || 0);
        };

        let appCounts = {};
        if (showCounts) {
            appCounts = fromPairs(
                toPairs(user.apps)
                    .filter(([key, appConfig]) => appConfig.badge_count)
                    .map(([key, { badge_count, badge_count_details }]) => [key, { badge_count, badge_count_details }])
            );
        }
        const switcher = (
            <StyledCard className='card card-switcher'>
                <div className='card-body'>
                    {apps ? (
                        groupedApps.map(([groupLabel, groupApps], index) => {
                            return (
                                <React.Fragment key={index}>
                                    <Row className='section-title'>
                                        <Col>
                                            <h3 className='border-bottom'>
                                                {groupLabel}
                                            </h3>
                                        </Col>
                                    </Row>
                                    <Row gutters={false}>
                                        {groupApps.sort(sortByOrder).map((app, i) => {
                                            let label = app.label;
                                            if (label.startsWith(groupLabel)) {
                                                label = label.replace(groupLabel, '');
                                            }

                                            return (
                                                <AppLink
                                                    key={app.key}
                                                    id={app.key}
                                                    icon={app.icon}
                                                    label={label}
                                                    count={appCounts[app.key]}
                                                    active={currentApp === app.key}
                                                    path={app.path}
                                                    callback={this.postSelectCallback}
                                                    tabIndex={
                                                        index.toString() + i.toString()
                                                    }
                                                />
                                            );
                                        })}
                                    </Row>
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <span>
                            <AppLink
                                icon='th'
                                label='Dashboard'
                                path='/'
                                callback={this.postSelectCallback}
                                tabIndex={1}
                            />

                            <AppLink
                                icon='user-circle'
                                label='Profile'
                                path='/profile'
                                callback={this.postSelectCallback}
                                tabIndex={2}
                            />
                        </span>
                    )}
                </div>
            </StyledCard>
        );

        if (inModal === false) {
            return switcher;
        }

        return <ModalBackground>{switcher}</ModalBackground>;
    }
}

export default AppSwitcher;
